import * as THREE from "three";
import { worldOnSphereFromNorm } from "../../../lib/equirectSphereNorm.ts";

const DEG = Math.PI / 180;

const _hotspotWorld = new THREE.Vector3();
const _projected = new THREE.Vector3();
const _projCamera = new THREE.PerspectiveCamera(75, 16 / 9, 0.01, 200);

export function normalizeAngleDeg(deg) {
  let a = deg;
  while (a > 180) a -= 360;
  while (a < -180) a += 360;
  return a;
}

/**
 * Equirect UV (0..1) → patrol hotspot yaw/pitch (degrees).
 * Matches scale(-1, 1, 1) inner sphere (patrolEquirectRenderer).
 */
export function equirectNormToPatrolHotspotDeg(normX, normY) {
  const world = worldOnSphereFromNorm(normX, normY, 1);
  const relYawRad = Math.atan2(world.x, -world.z);
  const relPitchRad = Math.asin(
    Math.max(-1, Math.min(1, world.y)),
  );
  return {
    yaw_deg: normalizeAngleDeg(-(relYawRad / DEG)),
    pitch_deg: normalizeAngleDeg(-(relPitchRad / DEG)),
  };
}

/**
 * World-space point on the inner equirect sphere → UV (0..1).
 * Same mapping as `patrolHotspotDegToEquirectNorm` (BackSide-era yaw convention).
 */
export function spherePointToEquirectNorm(px, py, pz) {
  const len = Math.hypot(px, py, pz);
  if (!Number.isFinite(len) || len < 1e-6) return null;
  const wx = px / len;
  const wy = py / len;
  const wz = pz / len;
  const thetaT = Math.acos(Math.max(-1, Math.min(1, wy)));
  let phi = Math.atan2(wz, -wx);
  if (phi < 0) phi += Math.PI * 2;
  return {
    normX: Math.min(1, Math.max(0, phi / (Math.PI * 2))),
    normY: Math.min(1, Math.max(0, thetaT / Math.PI)),
  };
}

/**
 * Patrol hotspot yaw/pitch → equirect UV.
 * Unchanged from BackSide era so existing saved angles keep their meaning.
 */
export function patrolHotspotDegToEquirectNorm(yawDeg, pitchDeg) {
  const relYawRad = -normalizeAngleDeg(yawDeg) * DEG;
  const relPitchRad = -Number(pitchDeg) * DEG;
  const wx = Math.sin(relYawRad) * Math.cos(relPitchRad);
  const wy = Math.sin(relPitchRad);
  const wz = -Math.cos(relYawRad) * Math.cos(relPitchRad);
  const thetaT = Math.acos(Math.max(-1, Math.min(1, wy)));
  let phi = Math.atan2(wz, -wx);
  if (phi < 0) phi += Math.PI * 2;
  return {
    normX: Math.min(1, Math.max(0, phi / (Math.PI * 2))),
    normY: Math.min(1, Math.max(0, thetaT / Math.PI)),
  };
}

/**
 * World position on the patrol 360° sphere for stored yaw/pitch.
 * Bridges saved angles (BackSide-era UV) to scale(-1, 1, 1) geometry.
 */
export function patrolHotspotWorldPosition(
  yawDeg,
  pitchDeg,
  radius = 100,
  target = _hotspotWorld,
) {
  const { normX, normY } = patrolHotspotDegToEquirectNorm(yawDeg, pitchDeg);
  const w = worldOnSphereFromNorm(normX, normY, radius);
  return target.set(w.x, w.y, w.z);
}

/**
 * Viewer-relative yaw for link hotspot / overlays.
 */
export function patrolHotspotRelativeYawDeg(viewerYawDeg, hotspotYawDeg) {
  return normalizeAngleDeg(
    normalizeAngleDeg(viewerYawDeg) - normalizeAngleDeg(hotspotYawDeg),
  );
}

/** Viewer-relative pitch (positive = hotspot below view center). */
export function patrolHotspotRelativePitchDeg(viewerPitchDeg, hotspotPitchDeg) {
  return viewerPitchDeg - hotspotPitchDeg;
}

/**
 * Project a saved hotspot to screen % using the same camera as Patrol360Player
 * (YXZ, inner sphere with scale(-1, 1, 1) from patrolEquirectRenderer).
 */
export function projectPatrolHotspotToScreen({
  hotspotYawDeg,
  hotspotPitchDeg = -14,
  viewerYawDeg,
  viewerPitchDeg = 0,
  verticalFovDeg = 75,
  aspect = 16 / 9,
}) {
  patrolHotspotWorldPosition(hotspotYawDeg, hotspotPitchDeg, 100, _hotspotWorld);

  _projCamera.fov = verticalFovDeg;
  _projCamera.aspect = Math.max(aspect, 0.01);
  _projCamera.near = 0.01;
  _projCamera.far = 200;
  _projCamera.position.set(0, 0, 0);
  _projCamera.rotation.order = "YXZ";
  _projCamera.rotation.y = THREE.MathUtils.degToRad(viewerYawDeg);
  _projCamera.rotation.x = THREE.MathUtils.degToRad(
    Math.max(-89, Math.min(89, viewerPitchDeg)),
  );
  _projCamera.updateProjectionMatrix();
  _projCamera.updateMatrixWorld(true);

  _projected.copy(_hotspotWorld).project(_projCamera);

  const relYaw = patrolHotspotRelativeYawDeg(viewerYawDeg, hotspotYawDeg);
  const relPitch = patrolHotspotRelativePitchDeg(
    viewerPitchDeg,
    hotspotPitchDeg,
  );

  if (_projected.z > 1) {
    return {
      visible: false,
      xPct: 50,
      yPct: 50,
      relYawDeg: relYaw,
      chevronRotationDeg: relYaw,
    };
  }

  const xPct = (_projected.x * 0.5 + 0.5) * 100;
  const yPct = (-_projected.y * 0.5 + 0.5) * 100;

  return {
    visible: true,
    xPct: Math.max(4, Math.min(96, xPct)),
    yPct: Math.max(6, Math.min(94, yPct)),
    relYawDeg: relYaw,
    chevronRotationDeg: relYaw,
  };
}

/** Fixed floor Y; horizontal slide + chevron spin follow route bearing only. */
const LINK_HOTSPOT_FIXED_TOP_PCT = 68;
const LINK_HOTSPOT_MAX_H_SLIDE_PCT = 30;

/**
 * Screen layout for link hotspot: fixed vertical position, moves horizontally
 * along the pin route direction as the user pans (does not drift off-route).
 */
export function getPatrolLinkHotspotScreenLayout({
  hotspotYawDeg,
  viewerYawDeg,
  viewerPitchDeg = 0,
  verticalFovDeg = 75,
  aspect = 16 / 9,
}) {
  const relYaw = patrolHotspotRelativeYawDeg(viewerYawDeg, hotspotYawDeg);

  const directionMatched = isPatrolHotspotDirectionMatched(
    viewerYawDeg,
    hotspotYawDeg,
    viewerPitchDeg,
  );

  const hFovRad =
    2 * Math.atan(Math.tan((verticalFovDeg * DEG) / 2) * aspect);
  const hFovDeg = hFovRad / DEG;
  const halfH = hFovDeg / 2;

  let xPct = 50;
  if (Math.abs(relYaw) <= halfH * 1.05) {
    const slide =
      (Math.tan(relYaw * DEG) / Math.tan(halfH * DEG)) *
      LINK_HOTSPOT_MAX_H_SLIDE_PCT;
    xPct = 50 + slide;
  } else {
    xPct =
      relYaw > 0
        ? 50 + LINK_HOTSPOT_MAX_H_SLIDE_PCT
        : 50 - LINK_HOTSPOT_MAX_H_SLIDE_PCT;
  }

  return {
    visible: directionMatched,
    xPct: Math.max(8, Math.min(92, xPct)),
    yPct: LINK_HOTSPOT_FIXED_TOP_PCT,
    chevronRotationDeg: relYaw,
    directionMatched,
  };
}

export function isPatrolHotspotDirectionMatched(
  viewerYawDeg,
  hotspotYawDeg,
  viewerPitchDeg = 0,
  toleranceYawDeg = 38,
  maxPitchDeg = 35,
) {
  if (Math.abs(viewerPitchDeg) > maxPitchDeg) {
    return false;
  }

  const relYaw = patrolHotspotRelativeYawDeg(viewerYawDeg, hotspotYawDeg);

  return Math.abs(relYaw) <= toleranceYawDeg;
}
